'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FunnelState, getAssignedStepConfig } from '../../core';
import { useCheckoutQuery } from '../hooks/useCheckoutQuery';
import { useOrderQuery } from '../hooks/useOrderQuery';

interface FunnelScriptInjectorProps extends FunnelState { }

type ParsedElement =
  | { type: 'external'; src: string; async?: boolean; defer?: boolean }
  | { type: 'inline'; code: string }
  | { type: 'noscript'; html: string };

/**
 * Parse script content that may contain multiple <script> and <noscript> tags.
 * Handles: external scripts (<script src="...">), inline scripts, noscript blocks,
 * and bare JS without any HTML tags.
 */
function parseScriptContent(content: string): ParsedElement[] {
  const trimmed = content.trim();

  // If content doesn't contain any HTML script/noscript tags, treat as raw JS
  if (!/<(?:script|noscript)[\s>]/i.test(trimmed)) {
    return trimmed ? [{ type: 'inline', code: trimmed }] : [];
  }

  const elements: ParsedElement[] = [];
  const tagRegex = /<(script|noscript)([^>]*)>([\s\S]*?)<\/\1>/gi;
  let lastIndex = 0;
  let match;

  while ((match = tagRegex.exec(trimmed)) !== null) {
    // Capture any bare text between tags (could be JS or HTML comments)
    const between = trimmed.slice(lastIndex, match.index).trim();
    // Skip HTML comments and empty strings
    if (between && !/^<!--[\s\S]*?-->$/.test(between)) {
      elements.push({ type: 'inline', code: between });
    }

    const [, tagName, attrs, body] = match;

    if (tagName.toLowerCase() === 'noscript') {
      if (body.trim()) {
        elements.push({ type: 'noscript', html: body.trim() });
      }
    } else {
      // Script tag — check for src attribute (external script)
      const srcMatch = attrs.match(/src=["']([^"']+)["']/i);
      if (srcMatch) {
        elements.push({
          type: 'external',
          src: srcMatch[1],
          async: /\basync\b/i.test(attrs),
          defer: /\bdefer\b/i.test(attrs),
        });
      }
      // Inline content (can exist alongside src, though unusual)
      if (body.trim()) {
        elements.push({ type: 'inline', code: body.trim() });
      }
    }
    lastIndex = match.index + match[0].length;
  }

  // Trailing content after last tag
  const trailing = trimmed.slice(lastIndex).trim();
  if (trailing && !/^<!--[\s\S]*?-->$/.test(trailing)) {
    elements.push({ type: 'inline', code: trailing });
  }

  return elements;
}

/** Wrap inline JS in an error-handling IIFE */
function wrapInErrorHandler(scriptName: string, code: string): string {
  return `
    (function() {
      try {
        // Script: ${scriptName}
        ${code}
      } catch (error) {
        console.error('[TagadaPay] StepConfig script "${scriptName}" error:', error);
      }
    })();
  `;
}

/** Inject a DOM element at the specified position */
function injectAtPosition(element: Element, position: string) {
  switch (position) {
    case 'head-start':
      if (document.head.firstChild) {
        document.head.insertBefore(element, document.head.firstChild);
      } else {
        document.head.appendChild(element);
      }
      break;
    case 'head-end':
      document.head.appendChild(element);
      break;
    case 'body-start':
      if (document.body.firstChild) {
        document.body.insertBefore(element, document.body.firstChild);
      } else {
        document.body.appendChild(element);
      }
      break;
    case 'body-end':
    default:
      document.body.appendChild(element);
      break;
  }
}

/**
 * FunnelScriptInjector - Handles injection of funnel scripts into the page.
 *
 * This component:
 * - Sets up Tagada on the window object
 * - Injects scripts from stepConfig.scripts (NEW: from HTML injection, supports A/B variants)
 * - Falls back to context.script (LEGACY: from funnel context)
 * - Handles script positions: head-start, head-end, body-start, body-end
 * - Prevents duplicate script injection (handles React StrictMode)
 * - Cleans up scripts when context changes or component unmounts
 */
export function FunnelScriptInjector({ context, isInitialized }: FunnelScriptInjectorProps) {
  // Track last injected scripts to prevent duplicate execution
  const lastInjectedScriptRef = useRef<string | null>(null);
  const lastInjectedStepConfigScriptsRef = useRef<string | null>(null);

  // ─── Rich data bridging for window.Tagada (V1 parity) ───────────────
  // The funnel context's resources only hold thin references. The rich
  // checkout session (customer, items, amounts) and order data live in
  // the shared React Query cache via useCheckoutQuery / useOrderQuery.
  // Subscribe here and push onto window.Tagada so legacy scripts see the
  // full shape they got in V1. useCheckoutQuery needs an explicit
  // checkoutToken — read it from URL first, then fall back to the
  // funnel-context resources.
  const checkoutTokenFromUrlOrResources = useMemo(() => {
    if (typeof window !== 'undefined') {
      const fromUrl = new URLSearchParams(window.location.search).get('checkoutToken');
      if (fromUrl) return fromUrl;
    }
    const fromResources = (context?.resources as Record<string, unknown> | undefined)?.checkoutToken;
    return typeof fromResources === 'string' ? fromResources : undefined;
  }, [context?.resources]);
  const { checkout: richCheckoutData } = useCheckoutQuery({
    checkoutToken: checkoutTokenFromUrlOrResources,
    enabled: !!checkoutTokenFromUrlOrResources,
  });
  const orderIdFromUrl = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const fromUrl = new URLSearchParams(window.location.search).get('orderId');
    if (fromUrl) return fromUrl;
    // native-checkout v2 puts orderId in path /thankyou/<id>, not query.
    // Funnel context resources already carry it — use that as fallback.
    const fromResources = (context?.resources as Record<string, unknown> | undefined)?.order as
      | { id?: string }
      | undefined;
    return fromResources?.id;
  }, [context?.resources]);
  const { order: richOrderData } = useOrderQuery({ orderId: orderIdFromUrl, enabled: !!orderIdFromUrl });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // @ts-expect-error - accessing window property
    const T = window.Tagada;
    if (!T) return;
    const prevRichCheckoutSession = T._richCheckoutSession || null;
    const prevRichSummary = T._richOrderSummary || null;
    const prevRichOrder = T._richOrder || null;

    T._richCheckoutSession = richCheckoutData?.checkoutSession || null;
    T._richOrderSummary = richCheckoutData?.summary || null;
    T._richOrder = richOrderData || null;

    if (T._richCheckoutSession !== prevRichCheckoutSession) {
      T._fireCheckoutSessionUpdate?.({ checkoutSession: T._richCheckoutSession });
    }
    if (T._richOrderSummary !== prevRichSummary) {
      T._fireOrderSummaryUpdate?.({ orderSummary: T._richOrderSummary });
    }
    if (T._richOrder !== prevRichOrder) {
      T._fireOrderUpdate?.({ order: T._richOrder });
    }
  }, [richCheckoutData, richOrderData]);

  // Get stepConfig scripts from HTML injection or local config
  // Re-compute when initialized (local config loads async, so we need to re-check)
  const stepConfigScripts = useMemo(() => {
    const stepConfig = getAssignedStepConfig();
    const scripts = stepConfig?.scripts?.filter(s => s.enabled) || [];
    if (scripts.length > 0) {
      console.log('📜 [SDK] Found stepConfig scripts:', scripts.length, scripts.map(s => s.name));
    }
    return scripts;
  }, [isInitialized]); // Re-compute when funnel initializes (local config should be loaded by then)

  // Set up Tagada on window object - must be available before any scripts run
  // Memoized to prevent unnecessary recreations across re-renders
  const setupTagada = useCallback(() => {
    if (typeof window === 'undefined') return;

    // @ts-expect-error - Adding utilities to window
    if (window.Tagada) {
      // @ts-expect-error - Accessing window property
      const prev = window.Tagada;
      const prevResources = (prev.ressources || null) as Record<string, unknown> | null;
      const nextResources = (context?.resources || null) as Record<string, unknown> | null;

      // Update properties if Tagada already exists
      prev.pageType = context?.currentStepId || null;
      prev.isInitialized = isInitialized;
      prev.ressources = nextResources;
      prev.stepConfig = getAssignedStepConfig() || null;
      prev.funnel = context
        ? {
          sessionId: context.sessionId,
          funnelId: context.funnelId,
          currentStepId: context.currentStepId,
          previousStepId: context.previousStepId,
          ressources: context.resources,
        }
        : null;

      // Fire V1-compat listeners when specific resources change.
      // Support both V1 ("checkout") and native V2 ("checkoutSession") keys.
      const prevCheckout = prevResources?.checkoutSession || prevResources?.checkout;
      const nextCheckout = nextResources?.checkoutSession || nextResources?.checkout;
      if (prevCheckout !== nextCheckout) {
        prev._fireCheckoutSessionUpdate?.({ checkoutSession: nextCheckout || null });
      }
      const prevSummary = prevResources?.orderSummary || prevResources?.summary;
      const nextSummary = nextResources?.orderSummary || nextResources?.summary;
      if (prevSummary !== nextSummary) {
        prev._fireOrderSummaryUpdate?.({ orderSummary: nextSummary || null });
      }
      const prevOrder = prevResources?.order;
      const nextOrder = nextResources?.order;
      if (prevOrder !== nextOrder) {
        prev._fireOrderUpdate?.({ order: nextOrder || null });
      }
      return;
    }

    // Listener registries (captured via closures — outlive re-renders on window.Tagada)
    const checkoutSessionListeners = new Set<(d: { checkoutSession: unknown }) => void>();
    const orderSummaryListeners = new Set<(d: { orderSummary: unknown }) => void>();
    const orderListeners = new Set<(d: { order: unknown }) => void>();

    // @ts-expect-error - Adding utilities to window
    window.Tagada = {
      // Wait for DOM to be ready
      ready: (callback: () => void) => {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', callback);
        } else {
          callback();
        }
      },

      // Wait for window to be fully loaded AND funnel to be initialized
      loaded: (callback: () => void) => {
        const checkBothConditions = () => {
          const pageLoaded = document.readyState === 'complete';
          // @ts-expect-error - Accessing window property
          const funnelInitialized = window.Tagada?.isInitialized === true;

          if (pageLoaded && funnelInitialized) {
            callback();
            return true;
          }
          return false;
        };

        if (checkBothConditions()) {
          return;
        }

        let loadListener: (() => void) | null = null;
        let initCheckInterval: ReturnType<typeof setInterval> | null = null;
        let hasCalled = false;

        const cleanup = () => {
          if (loadListener) {
            window.removeEventListener('load', loadListener);
            loadListener = null;
          }
          if (initCheckInterval) {
            clearInterval(initCheckInterval);
            initCheckInterval = null;
          }
        };

        loadListener = () => {
          if (checkBothConditions() && !hasCalled) {
            hasCalled = true;
            cleanup();
          }
        };
        window.addEventListener('load', loadListener);

        initCheckInterval = setInterval(() => {
          if (checkBothConditions() && !hasCalled) {
            hasCalled = true;
            cleanup();
          }
        }, 100);

        setTimeout(() => {
          if (!hasCalled) {
            hasCalled = true;
            cleanup();
            if (document.readyState === 'complete') {
              callback();
            }
          }
        }, 10000);
      },

      delay: (callback: () => void, ms = 1000) => {
        setTimeout(callback, ms);
      },

      retry: (condition: () => boolean, callback: () => void, maxAttempts = 10, interval = 500) => {
        let attempts = 0;
        const check = () => {
          attempts++;
          if (condition() || attempts >= maxAttempts) {
            callback();
          } else {
            setTimeout(check, interval);
          }
        };
        check();
      },

      waitForElement: (selector: string, callback: (element: Element) => void, timeout = 10000) => {
        const element = document.querySelector(selector);
        if (element) {
          callback(element);
          return;
        }

        const observer = new MutationObserver(() => {
          const element = document.querySelector(selector);
          if (element) {
            observer.disconnect();
            callback(element);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });

        setTimeout(() => {
          observer.disconnect();
        }, timeout);
      },

      waitForElements: (selector: string, callback: (elements: NodeListOf<Element>) => void, timeout = 10000) => {
        const elements = document.querySelectorAll(selector);
        if (elements.length > 0) {
          callback(elements);
          return;
        }

        const observer = new MutationObserver(() => {
          const elements = document.querySelectorAll(selector);
          if (elements.length > 0) {
            observer.disconnect();
            callback(elements);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });

        setTimeout(() => {
          observer.disconnect();
        }, timeout);
      },

      pageType: context?.currentStepId || null,
      isInitialized: isInitialized,
      ressources: context?.resources || null,
      // Rich data caches bridged from React Query (useCheckoutQuery / useOrderQuery).
      // Populated by the useEffect above. Prefer these over thin funnel-context refs.
      _richCheckoutSession: null,
      _richOrderSummary: null,
      _richOrder: null,
      // V1-compat: Tagada.order and Tagada.checkout.session return full data.
      // Prefer React-Query-fetched data, fall back to funnel context resources.
      get order() {
        return this._richOrder || this.ressources?.order || null;
      },
      get checkout() {
        const ressources = this.ressources as Record<string, unknown> | null;
        const richSession = this._richCheckoutSession as Record<string, unknown> | null;
        const richSummary = this._richOrderSummary as Record<string, unknown> | null;
        const resource =
          richSession ||
          (ressources?.checkoutSession as Record<string, unknown> | undefined) ||
          (ressources?.checkout as Record<string, unknown> | undefined) ||
          null;
        const orderSummary =
          richSummary ||
          (ressources?.orderSummary as Record<string, unknown> | undefined) ||
          (ressources?.summary as Record<string, unknown> | undefined) ||
          null;
        if (!resource && !orderSummary) return null;
        return {
          ...(resource || {}),
          session: resource,
          orderSummary,
        };
      },
      funnel: context
        ? {
          sessionId: context.sessionId,
          funnelId: context.funnelId,
          currentStepId: context.currentStepId,
          previousStepId: context.previousStepId,
          ressources: context.resources,
        }
        : null,
      stepConfig: getAssignedStepConfig() || null,

      // V1-compat reactive listeners. Fire when the corresponding resource
      // reference changes in the funnel context (see update branch above).
      onCheckoutSessionUpdate: (cb: (d: { checkoutSession: unknown }) => void) => {
        checkoutSessionListeners.add(cb);
        return () => checkoutSessionListeners.delete(cb);
      },
      onOrderSummaryUpdate: (cb: (d: { orderSummary: unknown }) => void) => {
        orderSummaryListeners.add(cb);
        return () => orderSummaryListeners.delete(cb);
      },
      onOrderUpdate: (cb: (d: { order: unknown }) => void) => {
        orderListeners.add(cb);
        return () => orderListeners.delete(cb);
      },
      // Internal fire hooks used by the update branch
      _fireCheckoutSessionUpdate: (data: { checkoutSession: unknown }) =>
        checkoutSessionListeners.forEach(cb => { try { cb(data); } catch (e) { console.error('[Tagada] onCheckoutSessionUpdate listener threw:', e); } }),
      _fireOrderSummaryUpdate: (data: { orderSummary: unknown }) =>
        orderSummaryListeners.forEach(cb => { try { cb(data); } catch (e) { console.error('[Tagada] onOrderSummaryUpdate listener threw:', e); } }),
      _fireOrderUpdate: (data: { order: unknown }) =>
        orderListeners.forEach(cb => { try { cb(data); } catch (e) { console.error('[Tagada] onOrderUpdate listener threw:', e); } }),
    };
  }, [context, isInitialized]);

  useEffect(() => {
    // Only run in browser environment
    if (typeof document === 'undefined') {
      return;
    }

    // Set up Tagada utilities before injecting legacy script
    setupTagada();

    const scriptContent = context?.script;
    const scriptId = 'tagada-funnel-script';

    if (!scriptContent || !scriptContent.trim()) {
      // Clear ref if script is removed
      lastInjectedScriptRef.current = null;
      // Remove existing script if it exists
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
      return;
    }

    // Extract script content (remove <script> tags if present)
    let scriptBody = scriptContent.trim();

    // Check if script is wrapped in <script> tags
    const scriptTagMatch = scriptBody.match(/^<script[^>]*>([\s\S]*)<\/script>$/i);
    if (scriptTagMatch) {
      scriptBody = scriptTagMatch[1].trim();
    }

    // Skip if script body is empty after extraction
    if (!scriptBody) {
      return;
    }

    // Prevent duplicate injection of the same script content
    // This handles React StrictMode double-execution in development
    if (lastInjectedScriptRef.current === scriptBody) {
      return;
    }

    // Remove existing script if it exists (for script updates)
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    // Wrap script content with error handling and context checks
    // NOTE: We use indirect eval (0,eval)() so that SyntaxErrors in user scripts
    // are thrown at runtime (catchable by try/catch) rather than at parse time (uncatchable).
    // JSON.stringify safely escapes the script content (backticks, quotes, ${...}, etc.)
    const wrappedScript = '(function() {\n' +
      '  try {\n' +
      '    if (typeof document === "undefined") { console.error("[TagadaPay] Document not available"); return; }\n' +
      '    if (!window.Tagada) { console.error("[TagadaPay] Tagada not available"); return; }\n' +
      '    (0, eval)(' + JSON.stringify(scriptBody) + ');\n' +
      '  } catch (error) {\n' +
      '    console.error("[TagadaPay] Script execution error:", error);\n' +
      '  }\n' +
      '})();';

    // Create and inject new script element
    const scriptElement = document.createElement('script');
    scriptElement.id = scriptId;
    scriptElement.textContent = wrappedScript;
    document.body.appendChild(scriptElement);

    // Track this script content to prevent re-injection (handles React StrictMode double-execution)
    lastInjectedScriptRef.current = scriptBody;

    // Cleanup: remove script element but keep ref to prevent re-injection on StrictMode second run
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
      // Note: We intentionally DON'T clear lastInjectedScriptRef here
      // This prevents React StrictMode from re-injecting the same script on the second run
      // The ref will be cleared when script content actually changes (next effect run)
    };
  }, [context?.script, context?.currentStepId, isInitialized, setupTagada]);

  // Effect for NEW stepConfig.scripts (from HTML injection, supports A/B variants)
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // IMPORTANT: Set up Tagada BEFORE injecting any scripts
    // This ensures Tagada.ready() is always available when scripts run
    setupTagada();

    if (stepConfigScripts.length === 0) return;

    // Create a hash of current scripts to detect changes
    const scriptsHash = JSON.stringify(stepConfigScripts.map(s => ({ name: s.name, content: s.content, position: s.position })));

    // Check if scripts are actually in the DOM (handles React StrictMode cleanup)
    const existingScripts = document.querySelectorAll('[data-tagada-stepconfig-script]');
    const scriptsExistInDom = existingScripts.length > 0;

    // Skip ONLY if: same hash AND scripts actually exist in DOM
    // This handles React StrictMode where cleanup removes scripts but ref persists
    if (lastInjectedStepConfigScriptsRef.current === scriptsHash && scriptsExistInDom) {
      return;
    }

    // Remove any existing stepConfig scripts before re-injecting
    existingScripts.forEach(el => el.remove());

    // Inject each enabled script at its correct position
    stepConfigScripts.forEach((script, index) => {
      const position = script.position || 'head-end';
      const content = script.content.trim();
      if (!content) return;

      // Parse content into individual elements (handles multi-tag scripts)
      const parsed = parseScriptContent(content);

      parsed.forEach((element, elemIndex) => {
        const elemId = `tagada-stepconfig-script-${index}-${elemIndex}`;

        if (element.type === 'external') {
          const el = document.createElement('script');
          el.id = elemId;
          el.setAttribute('data-tagada-stepconfig-script', 'true');
          el.setAttribute('data-script-name', script.name);
          el.src = element.src;
          if (element.async) el.async = true;
          if (element.defer) el.defer = true;
          injectAtPosition(el, position);

        } else if (element.type === 'inline') {
          // Inline script — wrap in error handler
          const el = document.createElement('script');
          el.id = elemId;
          el.setAttribute('data-tagada-stepconfig-script', 'true');
          el.setAttribute('data-script-name', script.name);
          el.textContent = wrapInErrorHandler(script.name, element.code);
          injectAtPosition(el, position);

        } else if (element.type === 'noscript') {
          // Noscript block — inject as <noscript> element
          const el = document.createElement('noscript');
          el.id = elemId;
          el.setAttribute('data-tagada-stepconfig-script', 'true');
          el.innerHTML = element.html;
          injectAtPosition(el, position);
        }
      });
    });

    // Track injected scripts to prevent re-injection
    lastInjectedStepConfigScriptsRef.current = scriptsHash;

    // Cleanup: remove scripts on unmount
    // Note: We keep the ref intact but check DOM existence on re-mount
    // This handles both StrictMode (re-inject if cleanup removed them) and
    // real unmounts (scripts properly cleaned up)
    return () => {
      document.querySelectorAll('[data-tagada-stepconfig-script]').forEach(el => el.remove());
    };
  }, [stepConfigScripts, setupTagada]);

  // This component doesn't render anything
  return null;
}
